'use client'
import Link from 'next/link'
import Image from 'next/image'

import React, { useCallback, useEffect, useState, useRef, memo } from 'react'
import { PrismicRichText } from '@prismicio/react'
import { getCMMCLinkType } from '@/utils/getCMMCLinkType'
import { HubSearchProps, HubArticlePrismicDocument } from '@/types/hub'
import { FRAMEWORKS_WITH_CUSTOM_URLS } from '@/constants/hubFrameworks'

const SearchBox: React.FC<HubSearchProps> = memo(({ data, linkType, locale }) => {
  const [searchText, setSearchText] = useState('')
  const [filteredResults, setFilteredResults] = useState<HubArticlePrismicDocument[]>([])
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const performSearch = useCallback(
    (searchQuery: string) => {
      if (!searchQuery.trim()) {
        setFilteredResults([])
        setSelectedIndex(-1)
        return
      }

      const searchTerms = searchQuery.toLowerCase().split(' ').filter(Boolean)
      const filtered = data.filter((item) => {
        const titleText = (item.data as any).title?.[0]?.text?.toLowerCase() || ''
        return searchTerms.every((term) => titleText.includes(term))
      })

      setFilteredResults(filtered.slice(0, 10)) // Limit results for performance
      setSelectedIndex(-1) // Reset selection when results change
    },
    [data],
  )

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      performSearch(searchText)
    }, 300) // Debounce search

    return () => clearTimeout(timeoutId)
  }, [searchText, performSearch])

  // For Better Accessibility
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (filteredResults.length === 0) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((prev) => (prev < filteredResults.length - 1 ? prev + 1 : prev))
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1))
          break
        case 'Enter':
          if (selectedIndex >= 0 && selectedIndex < filteredResults.length) {
            e.preventDefault()
            const result = filteredResults[selectedIndex]
            const url = FRAMEWORKS_WITH_CUSTOM_URLS.includes(linkType as string)
              ? getCMMCLinkType(result.uid, linkType as string, locale)
              : (result.url ?? '')
            window.location.href = url
          }
          break
        case 'Escape':
          setFilteredResults([])
          setSelectedIndex(-1)
          searchInputRef.current?.blur()
          break
      }
    },
    [filteredResults, selectedIndex, linkType, locale],
  )

  return (
    <div className="relative mx-auto w-full px-3 pb-14 md:w-[420px] md:px-0">
      <Image
        src="/images/search.svg"
        alt=""
        aria-hidden="true"
        width={26}
        height={26}
        sizes="100vw"
        className="absolute left-6 top-[18px] md:left-5"
      />

      <label htmlFor="search" className="sr-only">
        Search FAQ and resources
      </label>
      <input
        ref={searchInputRef}
        autoComplete="off"
        type="search"
        name="search"
        id="search"
        aria-label="Search FAQ and resources"
        aria-describedby="search-description"
        aria-expanded={filteredResults.length > 0}
        aria-controls={filteredResults.length > 0 ? 'search-results-listbox' : undefined}
        aria-activedescendant={selectedIndex >= 0 ? `search-result-${selectedIndex}` : undefined}
        placeholder="Search FAQ and resources..."
        onChange={(e) => setSearchText(e.target.value)}
        onKeyDown={handleKeyDown}
        className="search-reset w-full rounded-md border border-primary-ink-10 bg-white py-4 pl-14 pr-4 text-xl text-primary-ink-85 placeholder:text-primary-ink-30 focus:border-primary-ink-10 focus:outline-0 focus:ring-0 md:pl-16"
      />
      <span id="search-description" className="sr-only">
        Search through FAQ articles and compliance resources
      </span>

      {filteredResults.length > 0 && (
        <ul
          id="search-results-listbox"
          role="listbox"
          aria-label="Search results"
          aria-live="polite"
          aria-atomic="false"
          className="absolute left-[50%] top-[65px] z-50 w-[300px] translate-x-[-50%] rounded-md border bg-white p-4 shadow-light backdrop-blur-25 md:w-[500px]">
          {filteredResults.map((result, index) => {
            const isSelected = index === selectedIndex
            return (
              <li
                key={`search-result-${result.uid}-${index}`}
                role="option"
                aria-selected={isSelected}
                id={`search-result-${index}`}
                className={`[&_a]:block [&_a]:rounded [&_a]:p-2 [&_a]:hover:bg-gray-50 [&_h1]:text-xl ${
                  isSelected ? 'bg-gray-100' : ''
                }`}>
                <Link
                  href={
                    FRAMEWORKS_WITH_CUSTOM_URLS.includes(linkType as string)
                      ? getCMMCLinkType(result.uid, linkType as string, locale)
                      : (result.url ?? '')
                  }
                  aria-describedby={`search-result-${index}`}
                  className={isSelected ? 'focus:outline-none' : ''}>
                  <PrismicRichText field={(result.data as any).title} />
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
})

SearchBox.displayName = 'SearchBox'
export default SearchBox
